'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/auth/turnstile-widget';
import { AuthInput, AuthButton, AuthLayout, SuccessState } from '@/components/auth/auth-layout';

export function ForgotPasswordForm() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileResetRef = useRef<(() => void) | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await forgotPassword(email, turnstileToken ?? undefined);
      setSent(true);
    } catch (error) {
      const { friendlyErrorMessage } = await import('@/lib/toast-error');
      toast.error(friendlyErrorMessage(error));
      turnstileResetRef.current?.();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout>
        <SuccessState
          title="Check your email"
          description="If an account exists for that email, we've sent password reset instructions."
        >
          <AuthButton variant="secondary" onClick={() => setSent(false)}>
            Try a different email
          </AuthButton>
          <Link href="/login">
            <AuthButton>Back to sign in</AuthButton>
          </Link>
        </SuccessState>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Link
        href="/login"
        className="mb-6 inline-flex items-center gap-1 text-sm text-white/40 transition-colors hover:text-white/70"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sign in
      </Link>

      <div className="mb-6 text-center">
        <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">
          Reset your password
        </h1>
        <p className="text-[15px] leading-relaxed text-white/40">
          Enter your email and we&apos;ll send a reset link.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
        />

        <TurnstileWidget
          onVerify={setTurnstileToken}
          onError={() => setTurnstileToken(null)}
          onExpire={() => setTurnstileToken(null)}
          resetRef={turnstileResetRef}
        />

        <div className="pt-2">
          <AuthButton
            type="submit"
            loading={loading}
            disabled={TURNSTILE_ENABLED && !turnstileToken}
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </AuthButton>
        </div>
      </form>

      <p className="mt-6 text-center text-sm text-white/25">
        Remember your password?{' '}
        <Link href="/login" className="text-white/50 hover:text-white/80">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
